import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Phase 1: everything is free, no Stripe activated. All paid-tier buttons send to /signup.
// Per CLAUDE.md: paid tiers turn on in Phase 2 once we hit ~500 reviews.

export default function Pricing() {
  const navigate = useNavigate();
  const { hash } = useLocation();

  // React Router doesn't auto-scroll to URL hashes. Honor /pricing#faq.
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  const plans = [
    {
      name: 'Bronze',
      price: '$4.99',
      per: 'per unlock',
      eyebrow: 'Pay as you go',
      bannerGradient: 'linear-gradient(135deg, #5E3A24 0%, #9C6B4B 18%, #C99478 35%, #E5BFA3 52%, #B58163 68%, #7E5535 85%, #4E2F1C 100%)',
      bannerTextColor: '#000',
      desc: 'Unlock one full profile when you need it. No subscription, no commitment.',
      features: [
        '1 profile unlock',
        'Lifetime access to that profile',
        'See full reviews, ratings, and rental history',
        'Pay only when you need it',
      ],
    },
    {
      name: 'Silver',
      price: '$9.99',
      per: '/month',
      eyebrow: 'Most popular',
      bannerGradient: 'linear-gradient(90deg, #8C9197 0%, #B6BBC2 22%, #DCE0E5 38%, #F0F2F5 50%, #D5D9DE 62%, #ABB1B8 78%, #82888F 100%)',
      bannerTextColor: '#0F2C4E',
      savings: 'Save 33% per unlock',
      desc: 'Three unlocks every month. Best for occasional renters and small property management.',
      features: [
        '3 profile unlocks per month',
        'Lifetime access to each unlocked profile',
        'Mix property management and tenants as you like',
        'Cancel anytime, no fees',
      ],
      highlight: true,
    },
    {
      name: 'Gold',
      price: '$24.99',
      per: '/month',
      eyebrow: 'Best for property management',
      bannerGradient: 'linear-gradient(135deg, #876F2C 0%, #C8A954 20%, #E5D38A 38%, #F2E5A8 52%, #D9C275 66%, #A88B3D 84%, #7B6529 100%)',
      bannerTextColor: '#000',
      savings: 'Save up to 80%',
      desc: 'Unlimited unlocks. For property management and property managers screening multiple tenants.',
      features: [
        'Unlimited profile unlocks',
        'Lifetime access stays even after you cancel',
        'Ideal for managers and high-volume property management',
        'Cancel anytime, no fees',
      ],
    },
  ];

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      {/* Hero */}
      <div style={{ background: '#F5EFE3', padding: '48px 24px 40px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, color: '#0F2C4E', marginBottom: 12, fontWeight: 400 }}>
          Rent with confidence.
        </h1>
        <p style={{ fontSize: 15, color: '#6B6358', maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
          Know who you’re renting with — or renting to — before you commit.
          Free to write a review... pay to read a review.
        </p>
        <div style={{
          display: 'inline-block', marginTop: 18, background: '#C9972A',
          color: '#fff', fontSize: 12, fontWeight: 700,
          padding: '6px 14px', borderRadius: 50, letterSpacing: '0.04em',
          boxShadow: '0 2px 8px rgba(201,151,42,0.30)',
        }}>
          LAUNCH PHASE — EVERYTHING IS FREE
        </div>
      </div>

      <div className="container" style={{ paddingTop: 32, paddingBottom: 48, maxWidth: 1100 }}>

        {/* Plans — horizontal row of three cards (Bronze / Silver / Gold) */}
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
          {plans.map((plan, i) => (
            <div
              key={i}
              style={{
                flex: '1 1 280px',
                minWidth: 0,
                background: '#FAF6EC',
                borderRadius: 16,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid rgba(184,137,58,0.22)',
                boxShadow: plan.highlight ? '0 12px 28px rgba(120,90,30,0.20)' : '0 4px 12px rgba(120,90,30,0.10)',
                transform: plan.highlight ? 'translateY(-4px)' : 'none',
                transition: 'transform 0.15s',
              }}
            >
              {/* Tier banner — metallic gradient with the tier name */}
              <div style={{
                background: plan.bannerGradient,
                color: plan.bannerTextColor,
                padding: '6px 16px',
                fontFamily: "'DM Serif Display', serif",
                fontSize: 18,
                fontWeight: 400,
                textAlign: 'center',
                letterSpacing: '0.08em',
                textShadow: plan.bannerTextColor === '#fff' ? '0 1px 3px rgba(0,0,0,0.45)' : '0 1px 1px rgba(255,255,255,0.35)',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              }}>
                {plan.name.toUpperCase()}
              </div>

              {/* Card content */}
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#C9972A', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                    {plan.eyebrow}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 30, fontWeight: 800, color: '#0F2C4E' }}>{plan.price}</span>
                    <span style={{ fontSize: 13, color: '#6B6358' }}>{plan.per}</span>
                  </div>
                  {plan.savings && (
                    <div style={{ display: 'inline-block', marginTop: 8, background: 'rgba(201,151,42,0.18)', color: '#C9972A', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 50, letterSpacing: '0.04em' }}>
                      {plan.savings}
                    </div>
                  )}
                </div>
                <p style={{ fontSize: 13, color: '#2C2C2C', marginBottom: 16, lineHeight: 1.6 }}>{plan.desc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20, flex: 1 }}>
                  {plan.features.map((f, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(15,44,78,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <svg width={10} height={10} viewBox="0 0 12 12">
                          <polyline points="2,6 5,9 10,3" fill="none" stroke="#0F2C4E" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.5 }}>{f}</span>
                    </div>
                  ))}
                </div>
                {/* Credit-report add-on — available on every tier */}
                <div style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  background: 'rgba(201,151,42,0.10)',
                  border: '1px dashed rgba(201,151,42,0.55)',
                  borderRadius: 10,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 18, lineHeight: 1 }}>📄</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#C9972A', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                      Add-on
                    </div>
                    <div style={{ fontSize: 12, color: '#2C2C2C', lineHeight: 1.45 }}>
                      Tenant credit report — <b>$35</b>/report
                      <span style={{ display: 'block', fontSize: 11, color: '#6B6358' }}>Free during launch · soft inquiry · tenant authorizes</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/signup')}
                  style={{
                    alignSelf: 'center',
                    padding: '6px 14px',
                    borderRadius: 50,
                    border: 'none',
                    background: plan.bannerGradient,
                    color: plan.bannerTextColor,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.20)',
                    textShadow: plan.bannerTextColor === '#fff' ? '0 1px 3px rgba(0,0,0,0.45)' : '0 1px 1px rgba(255,255,255,0.35)',
                  }}
                >
                  Free during launch →
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Verified Badge banner — spans below all three tiers */}
        <div style={{
          marginTop: 24,
          padding: '18px 22px',
          background: '#FAF6EC',
          border: '1px dashed rgba(201,151,42,0.55)',
          borderRadius: 16,
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          boxShadow: '0 2px 8px rgba(120,90,30,0.08)',
        }}>
          <div style={{
            fontSize: 22, fontWeight: 800, color: '#fff',
            background: '#C9972A', width: 44, height: 44, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(201,151,42,0.30)', flexShrink: 0,
          }}>★</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: '#0F2C4E', fontWeight: 400, lineHeight: 1.3 }}>
              Learn how to earn a RenterRep badge to place on your website or social media accounts.
            </div>
            <div style={{ fontSize: 12, color: '#6B6358', marginTop: 4 }}>
              For property management and property managers in good standing. Eligibility: ⭐ 4.0+ avg · 3+ reviews · account 60+ days.
            </div>
          </div>
          <a href="mailto:support@renterrep.com?subject=Verified%20Badge%20eligibility" style={{
            background: '#0F2C4E', color: '#fff', textDecoration: 'none',
            padding: '6px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700,
            letterSpacing: '0.04em', flexShrink: 0,
            boxShadow: '0 2px 8px rgba(15,44,78,0.30)',
          }}>
            Learn more →
          </a>
        </div>

        {/* FAQ */}
        <div id="faq" style={{ marginTop: 40, scrollMarginTop: 80 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0F2C4E', marginBottom: 20, textAlign: 'center' }}>Common questions</h2>
          {[
            { q: 'During launch, is everything really free?', a: 'Yes. While we’re building review density, every unlock is free for everyone. Paid tiers turn on once we hit a critical mass of reviews — until then, post, browse, and unlock freely.' },
            { q: 'Who pays — property management or tenants?', a: 'Neither, and both, depending on what you’re doing. Anyone can post reviews for free, forever — tenant or property management. Searching and unlocking other people’s reviews is what costs money, on either side.' },
            { q: 'Is it really free to post a review?', a: 'Always. Posting reviews is free for everyone, on every tier, forever. You also earn +1 free profile unlock for every review you write.' },
            { q: 'What’s the difference between Bronze, Silver, and Gold?', a: 'Bronze is pay-as-you-go ($4.99 per unlock) with no subscription. Silver ($9.99/mo) gives you 3 unlocks every month — best for occasional use. Gold ($24.99/mo) gives unlimited unlocks, ideal for property management screening multiple tenants.' },
            { q: 'Do unlocked profiles stay unlocked?', a: 'Yes. Once you unlock a profile on any plan, you keep access for life — even if you cancel a Silver or Gold subscription.' },
            { q: 'Can I cancel anytime?', a: 'Yes. Cancel Silver or Gold anytime with no fees. Bronze is a one-time purchase, so there’s nothing to cancel.' },
            { q: 'Is my payment secure?', a: 'When paid tiers turn on, payments will be processed by Stripe — the industry standard for secure online payments. RenterRep never stores card details.' },
          ].map((item, i) => (
            <div key={i} style={{
              background: '#FAF6EC',
              border: '1px solid rgba(184,137,58,0.22)',
              borderRadius: 16,
              boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
              padding: 16, marginBottom: 10,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F2C4E', marginBottom: 6 }}>{item.q}</div>
              <div style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.6 }}>{item.a}</div>
            </div>
          ))}
        </div>

        {/* CTA — matches homepage CTA aesthetic */}
        <div style={{ padding: '36px 16px 16px', textAlign: 'center', marginTop: 16 }}>
          <span style={{
            display: 'inline-block',
            background: '#B8893A',
            color: '#fff', fontSize: 16, fontWeight: 700,
            padding: '12px 36px', borderRadius: 50,
            boxShadow: '0 4px 16px rgba(184,137,58,0.32)',
            marginBottom: 12,
          }}>
            Ready to get started?
          </span>
          <p style={{ fontSize: 13, color: '#6B6358', marginBottom: 20 }}>Sign up free. Post reviews free. Everything is free during launch.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/signup')} style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 50, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Sign Up Free
            </button>
            <a href={'mailto:support@renterrep.com'} style={{ background: 'transparent', color: '#0F2C4E', border: '1.5px solid #0F2C4E', padding: '12px 28px', borderRadius: 50, fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
